// Git lessons - base set (20 lessons)
const baseGitLessons = [
  {
    title: "Initializing a Repository",
    description: "The first step in using Git is to initialize a repository in your project directory.",
    task: "Enter the command to initialize a new Git repository.",
    hint: "This creates a hidden .git directory to track changes.",
    example: "git <command>",
    answer: "git init",
    tips: [
      "This creates a .git directory in your current directory",
      "You only need to do this once per project"
    ],
    commandRef: {
      command: "git init",
      description: "Create an empty Git repository or reinitialize an existing one. This command creates a .git subdirectory containing all necessary repository files.",
      syntax: "git init [directory]",
      options: [
        { flag: "--bare", description: "Create a bare repository (no working directory, for servers)" },
        { flag: "--initial-branch=<name>", description: "Set the name for the initial branch (default: main or master)" },
        { flag: "--template=<dir>", description: "Specify the directory from which templates will be used" },
        { flag: "-q, --quiet", description: "Only print error and warning messages" }
      ],
      examples: [
        { code: "git init", description: "Initialize a new repository in the current directory" },
        { code: "git init my-project", description: "Create a new directory 'my-project' and initialize a repository in it" },
        { code: "git init --initial-branch=main", description: "Initialize with 'main' as the default branch name" }
      ]
    }
  },
  {
    title: "Checking Status",
    description: "You can check the current status of your repository to see which files are tracked, modified, or untracked.",
    task: "Enter the command to check the status of your repository.",
    hint: "This command shows which files are staged, unstaged, or untracked.",
    example: "git <command>",
    answer: "git status",
    tips: [
      "This is one of the most frequently used Git commands",
      "It helps you understand what Git sees in your working directory"
    ],
    commandRef: {
      command: "git status",
      description: "Show the working tree status. Displays paths that have differences between the index and the current HEAD commit, and paths that are not tracked by Git.",
      syntax: "git status [options] [--] [pathspec...]",
      options: [
        { flag: "-s, --short", description: "Give output in short format for a more compact view" },
        { flag: "-b, --branch", description: "Show branch and tracking info even in short format" },
        { flag: "--ignored", description: "Show ignored files as well" },
        { flag: "-u, --untracked-files", description: "Show untracked files (no=hide, normal=show, all=show all)" }
      ],
      examples: [
        { code: "git status", description: "Show full status of the working directory" },
        { code: "git status -s", description: "Show status in short format (M=modified, A=added, ??=untracked)" },
        { code: "git status -sb", description: "Show short status with branch information" }
      ]
    }
  },
  {
    title: "Adding Files",
    description: "Before committing changes, you need to stage them with the add command.",
    task: "Enter the command to add a file named 'index.html' to the staging area.",
    hint: "This prepares the specified file to be included in the next commit.",
    example: "git add filename",
    answer: "git add index.html",
    tips: [
      "Use git add . to stage all changes",
      "Staging lets you choose exactly what changes to include in each commit"
    ],
    commandRef: {
      command: "git add",
      description: "Add file contents to the staging area (index). This command updates the index using the current content found in the working tree, preparing the content for the next commit.",
      syntax: "git add [options] [--] [pathspec...]",
      options: [
        { flag: "-A, --all", description: "Stage all changes (new, modified, and deleted files)" },
        { flag: "-p, --patch", description: "Interactively choose hunks of patch to stage" },
        { flag: "-n, --dry-run", description: "Show what would be added without actually adding" },
        { flag: "-u, --update", description: "Only stage modified and deleted files, not new files" }
      ],
      examples: [
        { code: "git add index.html", description: "Stage a specific file for commit" },
        { code: "git add .", description: "Stage all changes in the current directory and subdirectories" },
        { code: "git add *.js", description: "Stage all JavaScript files using a wildcard pattern" }
      ]
    }
  },
  {
    title: "Making Commits",
    description: "Committing saves your staged changes to the repository history.",
    task: "Enter the command to commit staged changes with the message 'Initial commit'.",
    hint: "You need to include a commit message that describes your changes.",
    example: "git commit -m 'message'",
    answer: ["git commit -m 'Initial commit'", "git commit -m \"Initial commit\""],
    tips: [
      "Commit messages should be descriptive of what changed",
      "Good commit messages help you and others understand the project history"
    ],
    commandRef: {
      command: "git commit",
      description: "Record changes to the repository. Creates a new commit containing the current contents of the index and stores it in the repository along with a log message describing the changes.",
      syntax: "git commit [options]",
      options: [
        { flag: "-m <msg>", description: "Use the given message as the commit message" },
        { flag: "-a, --all", description: "Automatically stage modified and deleted files before committing" },
        { flag: "--amend", description: "Replace the tip of the current branch by creating a new commit" },
        { flag: "-v, --verbose", description: "Show unified diff between HEAD and what would be committed" }
      ],
      examples: [
        { code: "git commit -m 'Add login feature'", description: "Commit staged changes with a message" },
        { code: "git commit -am 'Fix typo'", description: "Stage all tracked files and commit with a message" },
        { code: "git commit --amend -m 'New message'", description: "Modify the most recent commit message" }
      ]
    }
  },
  {
    title: "Viewing History",
    description: "You can view the commit history to see all previous changes.",
    task: "Enter the command to view the commit history.",
    hint: "This shows a log of all commits with their messages, authors, and dates.",
    example: "git <command>",
    answer: ["git log", "git log --oneline"],
    tips: [
      "Add --oneline for a compact view",
      "Press q to exit the log view if it's long"
    ],
    commandRef: {
      command: "git log",
      description: "Show commit logs. Lists commits reachable from the current HEAD, showing commit hash, author, date, and message for each commit.",
      syntax: "git log [options] [revision range] [[--] path...]",
      options: [
        { flag: "--oneline", description: "Show each commit on a single line with abbreviated hash" },
        { flag: "--graph", description: "Draw a text-based graphical representation of commit history" },
        { flag: "-n <number>", description: "Limit the number of commits to show" },
        { flag: "--author=<pattern>", description: "Show only commits by a specific author" }
      ],
      examples: [
        { code: "git log", description: "Show the full commit history" },
        { code: "git log --oneline -10", description: "Show the last 10 commits in compact format" },
        { code: "git log --graph --oneline --all", description: "Show a visual graph of all branches" }
      ]
    }
  },
  {
    title: "Viewing Changes",
    description: "You can see what changes have been made but not yet staged.",
    task: "Enter the command to show unstaged changes.",
    hint: "This displays the differences between your working directory and the staging area.",
    example: "git <command>",
    answer: "git diff",
    tips: [
      "Lines with a + are additions",
      "Lines with a - are deletions"
    ],
    commandRef: {
      command: "git diff",
      description: "Show changes between commits, commit and working tree, etc. By default, shows the differences between the working directory and the staging area.",
      syntax: "git diff [options] [<commit>] [--] [<path>...]",
      options: [
        { flag: "--staged, --cached", description: "Show changes staged for the next commit" },
        { flag: "--stat", description: "Show diffstat instead of full diff (files changed, insertions, deletions)" },
        { flag: "--name-only", description: "Show only names of changed files" },
        { flag: "--color-words", description: "Show word-level differences with color highlighting" }
      ],
      examples: [
        { code: "git diff", description: "Show unstaged changes in the working directory" },
        { code: "git diff HEAD~2", description: "Show changes between 2 commits ago and now" },
        { code: "git diff main feature", description: "Show differences between two branches" }
      ]
    }
  },
  {
    title: "Viewing Staged Changes",
    description: "You can also see what changes have been staged but not yet committed.",
    task: "Enter the command to show staged changes.",
    hint: "This displays the differences between the staging area and the last commit.",
    example: "git diff --<option>",
    answer: "git diff --staged",
    tips: [
      "You can also use git diff --cached",
      "This shows what will be included in your next commit"
    ],
    commandRef: {
      command: "git diff --staged",
      description: "Show the differences between the staging area (index) and the last commit. This shows exactly what changes will be included in your next commit.",
      syntax: "git diff --staged [options] [<commit>] [--] [<path>...]",
      options: [
        { flag: "--stat", description: "Show a summary of changes (files, insertions, deletions)" },
        { flag: "--name-only", description: "Show only the names of files that will be committed" },
        { flag: "--name-status", description: "Show file names with status (A=added, M=modified, D=deleted)" },
        { flag: "-w, --ignore-all-space", description: "Ignore whitespace when comparing lines" }
      ],
      examples: [
        { code: "git diff --staged", description: "Show all staged changes that will be committed" },
        { code: "git diff --cached --stat", description: "Show summary of staged changes" },
        { code: "git diff --staged src/", description: "Show staged changes only in the src directory" }
      ]
    }
  },
  {
    title: "Adding Remote Repositories",
    description: "You can connect your local repository to a remote repository to share your code.",
    task: "Enter the command to add a remote repository with the name 'origin' and URL 'https://github.com/username/repo.git'.",
    hint: "This creates a connection to the remote repository.",
    example: "git remote add name url",
    answer: "git remote add origin https://github.com/username/repo.git",
    tips: [
      "origin is the conventional name for the primary remote repository",
      "You can add multiple remote repositories with different names"
    ],
    commandRef: {
      command: "git remote add",
      description: "Add a new remote repository connection. This creates a named reference to a remote repository that you can use for fetching and pushing changes.",
      syntax: "git remote add <name> <url>",
      options: [
        { flag: "-f", description: "Fetch the remote immediately after adding it" },
        { flag: "-t <branch>", description: "Track only the specified branch instead of all branches" },
        { flag: "--mirror=fetch", description: "Set up the remote for mirroring (fetch all refs)" },
        { flag: "--tags", description: "Import all tags from the remote" }
      ],
      examples: [
        { code: "git remote add origin https://github.com/user/repo.git", description: "Add a remote named 'origin'" },
        { code: "git remote add upstream https://github.com/original/repo.git", description: "Add an upstream remote for forked repos" },
        { code: "git remote add -f backup git@backup-server:repo.git", description: "Add a remote and fetch immediately" }
      ]
    }
  },
  {
    title: "Viewing Remote Repositories",
    description: "You can see what remote repositories are connected to your local repository.",
    task: "Enter the command to list all remote repositories.",
    hint: "This shows the names and URLs of all remotes.",
    example: "git remote <command>",
    answer: ["git remote -v", "git remote --verbose"],
    tips: [
      "The -v option shows the URLs for fetch and push",
      "Without options, only the names are shown"
    ],
    commandRef: {
      command: "git remote",
      description: "Manage the set of remote repositories. Without arguments, shows a list of existing remotes. With -v, shows remote URLs for fetching and pushing.",
      syntax: "git remote [options] [subcommand]",
      options: [
        { flag: "-v, --verbose", description: "Show remote URLs in addition to names" },
        { flag: "show <name>", description: "Display detailed information about a specific remote" },
        { flag: "remove <name>", description: "Remove a remote and all its tracking branches" },
        { flag: "rename <old> <new>", description: "Rename a remote from old name to new name" }
      ],
      examples: [
        { code: "git remote -v", description: "List all remotes with their fetch and push URLs" },
        { code: "git remote show origin", description: "Show detailed info about the origin remote" },
        { code: "git remote remove old-remote", description: "Remove a remote that is no longer needed" }
      ]
    }
  },
  {
    title: "Pushing Changes",
    description: "You can upload your commits to a remote repository.",
    task: "Enter the command to push your commits to the 'main' branch on the 'origin' remote.",
    hint: "This uploads local commits to the remote repository.",
    example: "git push <remote> <branch>",
    answer: "git push origin main",
    tips: [
      "You might need to pull first if there are remote changes",
      "Some repositories use master instead of main for the default branch"
    ],
    commandRef: {
      command: "git push",
      description: "Update remote refs along with associated objects. Uploads local branch commits to the remote repository branch.",
      syntax: "git push [options] [<repository> [<refspec>...]]",
      options: [
        { flag: "-u, --set-upstream", description: "Set upstream tracking for the branch (use with first push)" },
        { flag: "--force, -f", description: "Force push even if remote has changes (use with caution!)" },
        { flag: "--tags", description: "Push all tags to the remote repository" },
        { flag: "--delete", description: "Delete the specified remote branch" }
      ],
      examples: [
        { code: "git push origin main", description: "Push local main branch to origin" },
        { code: "git push -u origin feature", description: "Push and set upstream tracking for a new branch" },
        { code: "git push origin --delete old-branch", description: "Delete a branch on the remote" }
      ]
    }
  },
  {
    title: "Pulling Changes",
    description: "You can download and integrate changes from a remote repository.",
    task: "Enter the command to pull changes from the 'main' branch on the 'origin' remote.",
    hint: "This downloads remote commits and merges them into your current branch.",
    example: "git pull <remote> <branch>",
    answer: "git pull origin main",
    tips: [
      "This is equivalent to git fetch followed by git merge",
      "Use git pull --rebase to rebase instead of merge"
    ],
    commandRef: {
      command: "git pull",
      description: "Fetch from and integrate with another repository or local branch. Incorporates changes from a remote repository into the current branch.",
      syntax: "git pull [options] [<repository> [<refspec>...]]",
      options: [
        { flag: "--rebase", description: "Rebase the current branch on top of the fetched branch instead of merging" },
        { flag: "--no-commit", description: "Perform the merge but don't create a merge commit" },
        { flag: "--ff-only", description: "Only update if the merge can be resolved as a fast-forward" },
        { flag: "-v, --verbose", description: "Be more verbose about what is happening" }
      ],
      examples: [
        { code: "git pull origin main", description: "Pull changes from origin's main branch" },
        { code: "git pull --rebase", description: "Pull and rebase local commits on top of remote changes" },
        { code: "git pull --ff-only origin main", description: "Pull only if it's a fast-forward merge" }
      ]
    }
  },
  {
    title: "Cloning Repositories",
    description: "You can create a copy of a remote repository on your local machine.",
    task: "Enter the command to clone a repository from 'https://github.com/username/repo.git'.",
    hint: "This creates a new directory with a copy of the repository.",
    example: "git clone <url>",
    answer: "git clone https://github.com/username/repo.git",
    tips: [
      "This automatically sets up the origin remote",
      "It also creates a working copy of all files"
    ],
    commandRef: {
      command: "git clone",
      description: "Clone a repository into a new directory. Creates a copy of an existing repository, including all files, branches, and commit history.",
      syntax: "git clone [options] <repository> [<directory>]",
      options: [
        { flag: "--depth <depth>", description: "Create a shallow clone with limited history (faster for large repos)" },
        { flag: "-b, --branch <name>", description: "Clone a specific branch instead of the default" },
        { flag: "--single-branch", description: "Clone only the history leading to the tip of a single branch" },
        { flag: "--recurse-submodules", description: "Initialize and clone submodules after cloning" }
      ],
      examples: [
        { code: "git clone https://github.com/user/repo.git", description: "Clone a repository into a new directory" },
        { code: "git clone --depth 1 https://github.com/user/repo.git", description: "Shallow clone with only the latest commit" },
        { code: "git clone -b develop https://github.com/user/repo.git", description: "Clone only the develop branch" }
      ]
    }
  },
  {
    title: "Creating Branches",
    description: "Branches allow you to work on different features or versions in isolation.",
    task: "Enter the command to create a new branch named 'feature'.",
    hint: "This creates a new branch pointer at your current commit.",
    example: "git branch <name>",
    answer: "git branch feature",
    tips: [
      "This creates the branch but doesn't switch to it",
      "Use git checkout -b feature to create and switch in one command"
    ],
    commandRef: {
      command: "git branch",
      description: "List, create, or delete branches. When used with a name argument, creates a new branch pointing to the current HEAD commit.",
      syntax: "git branch [options] [<branchname>]",
      options: [
        { flag: "-a, --all", description: "List both local and remote-tracking branches" },
        { flag: "-d, --delete", description: "Delete a branch (only if fully merged)" },
        { flag: "-D", description: "Force delete a branch even if not merged" },
        { flag: "-m, --move", description: "Rename a branch" }
      ],
      examples: [
        { code: "git branch feature", description: "Create a new branch named 'feature'" },
        { code: "git branch -a", description: "List all local and remote branches" },
        { code: "git branch -m old-name new-name", description: "Rename a branch" }
      ]
    }
  },
  {
    title: "Switching Branches",
    description: "You can switch between different branches to work on different features.",
    task: "Enter the command to switch to the 'feature' branch.",
    hint: "This updates your working directory to reflect the branch.",
    example: "git <command> <branch>",
    answer: ["git checkout feature", "git switch feature"],
    tips: [
      "git checkout is the traditional command",
      "git switch is a newer command introduced in Git 2.23"
    ],
    commandRef: {
      command: "git checkout / git switch",
      description: "Switch branches or restore working tree files. git checkout is the traditional command; git switch (Git 2.23+) is a newer, more focused alternative for switching branches.",
      syntax: "git checkout <branch> | git switch <branch>",
      options: [
        { flag: "-b <new-branch>", description: "Create a new branch and switch to it (checkout -b or switch -c)" },
        { flag: "-", description: "Switch to the previously checked out branch" },
        { flag: "--track", description: "Set up tracking for a remote branch when switching" },
        { flag: "-f, --force", description: "Force switch even with uncommitted changes (discards changes)" }
      ],
      examples: [
        { code: "git checkout feature", description: "Switch to an existing 'feature' branch" },
        { code: "git switch -c new-feature", description: "Create and switch to a new branch (Git 2.23+)" },
        { code: "git checkout -", description: "Switch back to the previous branch" }
      ]
    }
  },
  {
    title: "Listing Branches",
    description: "You can see all branches in your repository.",
    task: "Enter the command to list all local branches.",
    hint: "This shows all branches with the current one marked with an asterisk.",
    example: "git <command>",
    answer: "git branch",
    tips: [
      "Use git branch -r to see remote branches",
      "Use git branch -a to see both local and remote branches"
    ],
    commandRef: {
      command: "git branch",
      description: "List branches. Without arguments, shows all local branches with the current branch highlighted with an asterisk (*).",
      syntax: "git branch [options]",
      options: [
        { flag: "-r, --remotes", description: "List remote-tracking branches only" },
        { flag: "-a, --all", description: "List both local and remote-tracking branches" },
        { flag: "-v, --verbose", description: "Show commit hash and subject for each branch" },
        { flag: "--merged", description: "List branches that have been merged into current branch" }
      ],
      examples: [
        { code: "git branch", description: "List all local branches" },
        { code: "git branch -r", description: "List remote-tracking branches" },
        { code: "git branch -vv", description: "Show branches with tracking info and last commit" }
      ]
    }
  },
  {
    title: "Merging Branches",
    description: "You can combine changes from different branches.",
    task: "Enter the command to merge the 'feature' branch into your current branch.",
    hint: "This integrates changes from the specified branch into your current branch.",
    example: "git <command> <branch>",
    answer: "git merge feature",
    tips: [
      "Make sure you're on the right branch before merging",
      "Git will try to automatically merge changes, but may need your help resolving conflicts"
    ],
    commandRef: {
      command: "git merge",
      description: "Join two or more development histories together. Incorporates changes from the named commits into the current branch.",
      syntax: "git merge [options] <branch>",
      options: [
        { flag: "--no-ff", description: "Create a merge commit even when fast-forward is possible" },
        { flag: "--squash", description: "Combine all commits into one before merging" },
        { flag: "--abort", description: "Abort the current merge and restore the original state" },
        { flag: "-m <msg>", description: "Set the commit message for the merge commit" }
      ],
      examples: [
        { code: "git merge feature", description: "Merge the feature branch into current branch" },
        { code: "git merge --no-ff feature", description: "Merge with a merge commit (preserves branch history)" },
        { code: "git merge --squash feature", description: "Squash all feature commits into one" }
      ]
    }
  },
  {
    title: "Deleting Branches",
    description: "You can remove branches that are no longer needed.",
    task: "Enter the command to delete a branch named 'feature' after it has been merged.",
    hint: "This removes the branch reference.",
    example: "git branch <option> <name>",
    answer: ["git branch -d feature", "git branch --delete feature"],
    tips: [
      "The -d option only deletes fully merged branches",
      "Use -D to force deletion of unmerged branches"
    ],
    commandRef: {
      command: "git branch -d",
      description: "Delete a branch. The -d option deletes the branch only if it has been fully merged into its upstream branch or HEAD. Use -D to force delete.",
      syntax: "git branch -d <branchname>",
      options: [
        { flag: "-d, --delete", description: "Delete a branch (must be fully merged)" },
        { flag: "-D", description: "Force delete a branch even if not merged (shortcut for --delete --force)" },
        { flag: "-r", description: "Delete remote-tracking branches (local references, not the actual remote)" },
        { flag: "--list", description: "List branches matching a pattern" }
      ],
      examples: [
        { code: "git branch -d feature", description: "Delete the feature branch (if merged)" },
        { code: "git branch -D experiment", description: "Force delete an unmerged branch" },
        { code: "git push origin --delete feature", description: "Delete a branch on the remote server" }
      ]
    }
  },
  {
    title: "Viewing Branch Differences",
    description: "You can see what changes are in one branch compared to another.",
    task: "Enter the command to show differences between the 'feature' branch and the 'main' branch.",
    hint: "This displays what commits are in one branch but not the other.",
    example: "git diff branch1..branch2",
    answer: ["git diff main..feature", "git diff main...feature"],
    tips: [
      "Two dots (..) shows commits reachable from one branch but not the other",
      "Three dots (...) shows commits reachable from either branch but not both"
    ],
    commandRef: {
      command: "git diff (branches)",
      description: "Show differences between branches. The double-dot notation shows changes between two branch tips. The triple-dot shows changes since the branches diverged.",
      syntax: "git diff <branch1>..<branch2> | git diff <branch1>...<branch2>",
      options: [
        { flag: "--stat", description: "Show a summary of changes between branches" },
        { flag: "--name-only", description: "Show only the names of changed files" },
        { flag: "--no-index", description: "Compare two paths on the filesystem" },
        { flag: "-p, --patch", description: "Generate patch output (default)" }
      ],
      examples: [
        { code: "git diff main..feature", description: "Show all changes between main and feature branches" },
        { code: "git diff main...feature", description: "Show changes in feature since it branched from main" },
        { code: "git diff main..feature --stat", description: "Show summary of files changed between branches" }
      ]
    }
  },
  {
    title: "Stashing Changes",
    description: "You can temporarily save changes that you're not ready to commit.",
    task: "Enter the command to stash your current changes.",
    hint: "This saves your changes and reverts to the last commit.",
    example: "git <command> <subcommand>",
    answer: ["git stash", "git stash save", "git stash push"],
    tips: [
      "This is useful when you need to switch branches but aren't ready to commit",
      "Use git stash pop to apply the stashed changes and remove them from the stash"
    ],
    commandRef: {
      command: "git stash",
      description: "Stash changes in a dirty working directory. Saves your local modifications and reverts the working directory to match the HEAD commit.",
      syntax: "git stash [push] [options] [--] [<pathspec>...]",
      options: [
        { flag: "-m, --message <msg>", description: "Add a description to the stash for easy identification" },
        { flag: "-u, --include-untracked", description: "Also stash untracked files" },
        { flag: "-k, --keep-index", description: "Keep changes that are already staged" },
        { flag: "-p, --patch", description: "Interactively select hunks to stash" }
      ],
      examples: [
        { code: "git stash", description: "Stash all tracked changes" },
        { code: "git stash -m 'Work in progress on feature'", description: "Stash with a descriptive message" },
        { code: "git stash -u", description: "Stash including untracked files" }
      ]
    }
  },
  {
    title: "Applying Stashed Changes",
    description: "You can reapply previously stashed changes.",
    task: "Enter the command to apply the most recent stash and remove it from the stash list.",
    hint: "This brings back your saved changes and removes them from the stash.",
    example: "git stash <command>",
    answer: "git stash pop",
    tips: [
      "Use git stash apply to apply changes without removing them from the stash",
      "You can have multiple stashes and reference them by index: git stash pop stash@{1}"
    ],
    commandRef: {
      command: "git stash pop",
      description: "Apply stashed changes and remove them from the stash list. If conflicts occur, the stash is not removed from the stash list.",
      syntax: "git stash pop [--index] [<stash>]",
      options: [
        { flag: "--index", description: "Try to restore the staged state as well" },
        { flag: "stash@{n}", description: "Apply a specific stash by its index (0 is most recent)" },
        { flag: "apply", description: "Apply stash without removing it from the list" },
        { flag: "list", description: "List all stashed changes with their descriptions" }
      ],
      examples: [
        { code: "git stash pop", description: "Apply and remove the most recent stash" },
        { code: "git stash apply", description: "Apply the most recent stash but keep it in the list" },
        { code: "git stash pop stash@{2}", description: "Apply and remove a specific stash by index" }
      ]
    }
  }
];

// Expand the base lessons to ~100 items by repeating them
const gitLessons = [];
for (let i = 0; i < 100; i++) {
  gitLessons.push({ ...baseGitLessons[i % baseGitLessons.length] });
}

export default gitLessons;
